import { CommonModule } from '@angular/common';
import { Component, effect, inject, input, signal, ChangeDetectionStrategy } from '@angular/core';
import { OuiButtonModule } from 'omnium-ui/button';
import { OuiTagModule } from 'omnium-ui/tag';
import {
  InvestorArianeDump,
  InvestorBoxPlacement,
  InvestorImmoOptionIntention
} from '../../../../../../core/models/api.models';
import { DoublonsBusterApiService } from '../../../../../../core/services/doublons-buster-api.service';
import { StateCardComponent } from '../../../../shared/state-card/state-card.component';
import { formatBoolean, formatIdentity, formatText } from '../../investor-viewer-format.utils';
import { InvestorViewerAccordionComponent } from '../investor-viewer-accordion/investor-viewer-accordion.component';

interface ArianeDumpState {
  loading: boolean;
  result: InvestorArianeDump | null;
  error: string | null;
}

const emptyArianeDumpState: ArianeDumpState = { loading: false, result: null, error: null };

@Component({
  selector: 'dbw-investor-external-references-section',
  imports: [
    CommonModule,
    OuiButtonModule,
    OuiTagModule,
    StateCardComponent,
    InvestorViewerAccordionComponent
  ],
  templateUrl: './investor-external-references-section.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: [
    '../../investor-viewer-section.shared.scss',
    './investor-external-references-section.component.scss'
  ]
})
export class InvestorExternalReferencesSectionComponent {
  readonly investorId = input.required<number>();
  readonly boxPlacement = input<InvestorBoxPlacement | null>(null);
  readonly boxPlacementLoading = input(false);
  readonly boxPlacementError = input<string | null>(null);
  readonly immoOptionIntention = input<InvestorImmoOptionIntention | null>(null);
  readonly immoLoading = input(false);
  readonly immoError = input<string | null>(null);

  private readonly apiService = inject(DoublonsBusterApiService);

  protected readonly arianeDumpState = signal<ArianeDumpState>(emptyArianeDumpState);

  protected readonly formatText = formatText;
  protected readonly formatBoolean = formatBoolean;
  protected readonly formatIdentity = formatIdentity;

  constructor() {
    effect(
      () => {
        this.investorId();
        this.arianeDumpState.set(emptyArianeDumpState);
      },
      { allowSignalWrites: true }
    );
  }

  protected loadArianeDump(): void {
    const investorId = this.investorId();

    if (!investorId || this.arianeDumpState().loading) {
      return;
    }

    this.arianeDumpState.set({ loading: true, result: null, error: null });

    this.apiService.getInvestorArianeDump(investorId).subscribe({
      next: result => this.arianeDumpState.set({ loading: false, result, error: null }),
      error: error =>
        this.arianeDumpState.set({
          loading: false,
          result: null,
          error: error?.error?.detail ?? 'Impossible de charger le dump Ariane.'
        })
    });
  }

  protected formatArianeDumpJson(dumpJson: string | null | undefined): string {
    if (!dumpJson?.trim()) {
      return '';
    }

    try {
      return JSON.stringify(JSON.parse(dumpJson), null, 2);
    } catch {
      return dumpJson;
    }
  }

  protected getArianeTagStatus(): 'completed' | 'pending' | 'error' {
    const state = this.arianeDumpState();

    if (state.loading) {
      return 'pending';
    }

    if (state.error) {
      return 'error';
    }

    switch (state.result?.messageLevel) {
      case 'error':
        return 'error';
      case 'warning':
        return 'pending';
      default:
        return 'completed';
    }
  }

  protected getArianeTagLabel(): string {
    const state = this.arianeDumpState();

    if (state.loading) {
      return 'Ariane en cours';
    }

    if (state.error) {
      return 'Ariane erreur';
    }

    const result = state.result;

    if (!result) {
      return 'Ariane non chargé';
    }

    if (result.messageLevel === 'error') {
      return 'Ariane erreur';
    }

    if (result.messageLevel === 'warning') {
      return 'Ariane alerte';
    }

    return result.dumpJson ? 'Ariane présent' : 'Ariane absent';
  }

  protected getArianeSummary(): string {
    const state = this.arianeDumpState();

    if (state.loading) {
      return 'Chargement en cours';
    }

    if (state.error) {
      return state.error;
    }

    return state.result?.message ?? 'Dump non chargé';
  }

  protected getBoxPlacementTagStatus(): 'completed' | 'pending' | 'error' {
    if (this.boxPlacementLoading()) {
      return 'pending';
    }

    if (this.boxPlacementError()) {
      return 'error';
    }

    return this.boxPlacement()?.hasOperations ? 'error' : 'completed';
  }

  protected getBoxPlacementTagLabel(): string {
    if (this.boxPlacementLoading()) {
      return 'Box en cours';
    }

    if (this.boxPlacementError()) {
      return 'Box erreur';
    }

    return this.boxPlacement()?.hasOperations ? 'Box présente' : 'Box absente';
  }

  protected getBoxPlacementSummary(): string {
    if (this.boxPlacementLoading()) {
      return 'Chargement en cours';
    }

    if (this.boxPlacementError()) {
      return this.boxPlacementError() ?? 'Erreur de chargement';
    }

    const result = this.boxPlacement();

    if (!result) {
      return 'Informations non disponibles';
    }

    return result.hasOperations ? 'Opérations Box détectées' : 'Aucune opération Box';
  }

  protected getImmoTagStatus(): 'completed' | 'pending' | 'error' {
    if (this.immoLoading()) {
      return 'pending';
    }

    if (this.immoError()) {
      return 'error';
    }

    const level = this.immoOptionIntention()?.messageLevel;

    switch (level) {
      case 'error':
        return 'error';
      case 'warning':
        return 'pending';
      default:
        return 'completed';
    }
  }

  protected getImmoTagLabel(): string {
    if (this.immoLoading()) {
      return 'Immo en cours';
    }

    if (this.immoError()) {
      return 'Immo erreur';
    }

    const result = this.immoOptionIntention();

    if (!result) {
      return 'Immo attente';
    }

    if (result.messageLevel === 'error') {
      return 'Immo erreur';
    }

    if (result.messageLevel === 'warning') {
      return 'Immo alerte';
    }

    return result.hasOptionIntention ? 'Immo présente' : 'Immo absente';
  }

  protected getImmoSummary(): string {
    if (this.immoLoading()) {
      return 'Chargement en cours';
    }

    if (this.immoError()) {
      return this.immoError() ?? 'Erreur de chargement';
    }

    const result = this.immoOptionIntention();

    if (!result) {
      return 'Informations non disponibles';
    }

    const intention = result.hasOptionIntention ? 'intention présente' : 'aucune intention';
    const match = formatIdentity(result.matchedLastName, result.matchedFirstName, 'aucune correspondance');

    return `${intention} - ${match}`;
  }
}
